<script setup>
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import EncerramentoModal from 'dashboard/components/widgets/conversation/fandata/EncerramentoModal.vue';
import TabulacaoModal from 'dashboard/components/widgets/conversation/fandata/TabulacaoModal.vue';
import { useFandataTabulacao } from 'dashboard/composables/useFandataTabulacao';
import { useFandataResolverPendente } from 'dashboard/composables/useFandataResolverPendente';

const props = defineProps({
  // T-155d: a pendência com parceiro passou a viver neste menu. Quem abre o modal continua sendo
  // o MoreActions, que é o dono dele; aqui só disparamos o evento.
  pendente: { type: Boolean, default: false },
});

const emit = defineEmits(['marcar-pendencia', 'retomar-pendencia']);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const { aplicarTabulacao } = useFandataTabulacao();
const { consumirEncerramento } = useFandataResolverPendente();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

// T-168: reabrir chamado é iniciativa do cliente, não do agente. Regra da Tamara em 03/09: "o
// agente não pode reabrir um chamado... isso é até para a gente medir a qualidade do nosso
// atendimento". Se precisamos voltar ao torcedor, isso é contato ativo, e é outra coisa.
// A permissão no servidor cai junto; esconder o botão sozinho não seria regra, seria decoração.
const { ehAdmin } = useFandataPerfil();

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(resultado => {
    isLoading.value = false;
    // Recusa do servidor tem motivo escrito, e quem clicou precisa lê-lo: sem isto a tela
    // dizia "Estado da conversa mudou" para um pedido que voltou 403 (15/10).
    if (resultado && resultado.ok === false) {
      useAlert(resultado.erro || t('CONVERSATION.CHANGE_STATUS_ERRO'));
      return;
    }
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

// FanData (R1.23 + T-97, reunião 13/08; ajustado em T-99, reunião 20/08): não se fecha ticket
// sem tabulação. A trava fica em CIMA de cada caminho de resolve, e não dentro do toggleStatus,
// porque o toggleStatus também marca pendência e reabre, e nesses casos não há o que exigir.
//
// A tela aparece SEMPRE, e não só quando falta classificação, que foi como saiu na primeira
// versão. A Tamara pediu explicitamente: "todos os encerramentos eu preciso tabular... vem com
// uma pré-tabulação e na hora de encerrar, ela confirma ou não". Ou seja, o valor está na
// CONFIRMAÇÃO humana: sem ela, o relatório herda o palpite da IA sem ninguém ter olhado.
//
// Vale só para o encerramento feito por gente. Bot, automação e o futuro fechamento por
// inatividade (T-96) resolvem pela API e continuam passando: travar ali pararia a operação
// automática por um campo que ninguém está lá para preencher.
const tabulacaoModalRef = ref(null);
let acaoPendente = null;

const comTabulacao = (acao, motivo = 'encerrar') => {
  acaoPendente = acao;
  tabulacaoModalRef.value?.open({ motivo });
};

// T-155c: "o marcar com pendência precisa ter a opção de tabulação assim como a opção resolver,
// com o plus de direcionar para parceiro/área" (validação de 31/08). Reusa a mesma tela de
// tabulação do encerramento em vez de duplicar o formulário: são a mesma decisão, tomada em dois
// momentos diferentes do atendimento. Depois dela, o modal de parceiro abre no componente pai.
const pedirPendencia = () => {
  closeDropdown();
  comTabulacao(() => emit('marcar-pendencia'), 'pendencia');
};

const onTabulacaoConfirmada = async payload => {
  await aplicarTabulacao(payload);
  const acao = acaoPendente;
  acaoPendente = null;
  acao?.();
};

// FanData (T-13): encerrar SEM pedir avaliação ao torcedor. Grava fandata_csat no mesmo request
// atômico do resolve (status + custom_attributes juntos, antes do CSAT rodar no backend), e é
// esse valor que faz o backend desistir do envio mesmo onde a caixa tem a pesquisa ligada.
//
// O caminho contrário (forçar o envio onde a caixa tem a pesquisa DESLIGADA) foi removido: era
// o botão Resolver com outro nome, nas caixas em que a pesquisa está ligada, e nas outras
// passava por cima de uma decisão que é de quem configura o canal, não de quem encerra o caso.
const resolverSemAvaliacao = () => {
  closeDropdown();
  encerrar({ semCsat: true });
};

// T-234: os DOIS caminhos de encerramento passam pela MESMA sequência — tabulação, depois a
// caixa de mensagem ao torcedor, depois o resolve. A diferença entre eles é só se a pesquisa de
// satisfação sai.
//
// Foi isso que permitiu "Resolver com CSAT" VOLTAR ao menu. Ele tinha saído em 23/09 por
// repetir o botão ao lado; agora cada caminho tem tela própria, então a escolha é explícita em
// vez de escondida na configuração da caixa.
//
// "Encerrar com mensagem" deixou de ser item separado pela mesma razão: a caixa de texto passou
// a estar nos dois, então um item só para ela seria um terceiro nome para a mesma coisa.
//
// A ordem é deliberada: primeiro se classifica o que aconteceu, depois se escreve ao torcedor.
// O contrário faria o agente escrever a despedida e só então ser interrompido para tabular.
let encerrarSemCsat = false;

const encerrar = ({ semCsat }) => {
  encerrarSemCsat = semCsat;
  comTabulacao(() => {
    // Sem mensagem a escrever, não se abre caixa nenhuma: o conteúdo DO diálogo é só este campo,
    // então mostrá-lo vazio seria um clique a mais em todo encerramento para não fazer nada.
    if (!pedirMensagem.value) {
      resolveComMensagem('');
      return;
    }

    encerramentoModalRef.value?.open({ contatoTemEmail: contatoTemEmail.value });
  });
};

// FanData (T-83, ampliado no T-234): a mensagem ao torcedor no encerramento. Era um caminho
// à parte no menu; virou etapa dos DOIS caminhos. O texto viaja no mesmo request do resolve e o
// backend o consome ao montar o e-mail de encerramento.
const encerramentoModalRef = ref(null);
const contatoTemEmail = computed(() =>
  Boolean(currentChat.value?.meta?.sender?.email)
);

// QUANDO A CAIXA DE TEXTO APARECE (pedido do time do clube, teste de 07/10).
//
// Ela nasceu para um caso só: o ticket que foi para pendência com um parceiro, ficou esperando,
// e é encerrado depois com um aviso por e-mail. Só que ela passou a aparecer em TODO
// encerramento, e no atendimento comum o torcedor está lendo a conversa na hora: escrever ali
// uma despedida que vai por e-mail não faz sentido nenhum, e o campo virou ruído.
//
// As duas condições precisam valer juntas. Sem e-mail não há para onde mandar, e fora da
// pendência o torcedor está na conversa.
//
// ⚠️ LIMITE CONHECIDO: vale pela pendência ATUAL. Quem clicar em "Retomar" antes de encerrar
// perde o campo, porque retomar apaga as chaves de pendência do espelho (ver
// Fandata::CustomFields#pendencia_mirror). Se a operação precisar do campo nesse caso, a marca
// de "passou por pendência" tem de virar durável, e aí é mudança de dado, não de tela.
const pedirMensagem = computed(
  () => props.pendente && contatoTemEmail.value
);

const resolverComAvaliacao = () => {
  closeDropdown();
  encerrar({ semCsat: false });
};
// Chega aqui depois da tabulação já confirmada. A mensagem continua OPCIONAL: o agente pode
// confirmar com a caixa vazia e o encerramento segue igual.
//
// A trava de ATRIBUTOS OBRIGATÓRIOS do upstream continua valendo e vem por último, de propósito:
// ela abre uma terceira tela, e só faz sentido incomodar com ela quem já decidiu encerrar. Sem
// isto, o T-234 teria apagado em silêncio uma regra que não é nossa.
const resolveComMensagem = mensagem => {
  const atuais = currentChat.value.custom_attributes || {};
  const merged = { ...atuais };
  if (mensagem) merged.fandata_encerramento_msg = mensagem;
  if (encerrarSemCsat) merged.fandata_csat = 'sem';
  encerrarSemCsat = false;

  const { hasMissing, missing } = checkMissingAttributes(merged);
  if (hasMissing) {
    resolveAttributesModalRef.value?.open(missing, merged, {
      id: currentChat.value.id,
      snoozedUntil: null,
    });
    return;
  }

  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED, null, merged);
};

// O botão Resolver é o caminho curto do "com avaliação": mesma sequência, sem passar pelo menu.
// Vale também para o atalho de teclado, de propósito: um encerramento é um encerramento, e não
// pode escapar da tabulação só por ter vindo pelo teclado.
const onCmdResolveConversation = () => {
  encerrar({ semCsat: false });
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);

// T-234: pedido de encerramento vindo do clique direito na fila. A tela navegou até aqui; agora
// que ela existe, o pedido é consumido e o encerramento roda completo, com tabulação e mensagem.
// `immediate` cobre o caso de a conversa já estar aberta quando o menu foi usado.
watch(
  () => currentChat.value?.id,
  id => {
    if (id && consumirEncerramento(id)) encerrar({ semCsat: false });
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved && ehAdmin"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <!-- T-234: os dois caminhos de encerramento aparecem lado a lado, e cada um tem a mesma
             sequência (tabular, escrever ao torcedor, encerrar). "Resolver com CSAT" voltou
             em 25/09: tinha saído por repetir o botão, e agora a escolha é explícita. "Encerrar
             com mensagem" saiu, porque a caixa de texto passou a estar nos dois. -->
        <WootDropdownItem v-if="isOpen">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.RESOLVE_WITH_CSAT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-star"
            class="w-full"
            @click="resolverComAvaliacao"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="isOpen">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.RESOLVE_WITHOUT_CSAT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-check"
            class="w-full"
            @click="resolverSemAvaliacao"
          />
        </WootDropdownItem>
        <!-- T-155g: "Deixar pendente" (o status do Chatwoot) saiu daqui. A Tamara foi explícita
             em 03/09: o agente não pode deixar um torcedor pendente na fila dele; ou encerra, ou
             transfere. O que ficou no lugar é a pendência com parceiro, que é transferência. -->
        <WootDropdownItem v-if="!props.pendente">
          <Button
            :label="t('CONVERSATION.PENDENCIA.MARCAR')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-pause"
            class="w-full"
            @click="pedirPendencia"
          />
        </WootDropdownItem>
        <WootDropdownItem v-else>
          <Button
            :label="t('CONVERSATION.PENDENCIA.RETOMAR')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-play"
            class="w-full"
            @click="() => { closeDropdown(); emit('retomar-pendencia'); }"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <EncerramentoModal
      ref="encerramentoModalRef"
      @submit="resolveComMensagem"
    />
    <TabulacaoModal ref="tabulacaoModalRef" @submit="onTabulacaoConfirmada" />
  </div>
</template>
